import { type ChangeEvent, type ReactElement, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Checkbox, FormControlLabel, IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, TableSortLabel, TextField, Tooltip
} from "@mui/material";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  convertToIGrades, exportGradesToExcel,
  getComparator, hasSpaceOrSpecialCharAtEnd, hasSpaceOrSpecialCharAtStart,
  loadExcelFile,
  mergeLimeGrades
} from "./utils";
import WorkSheetSelector from "./workSheetSelector";
import {IGradeBase, IGrades, IMergeRecord, ISpecData, Order} from "./types";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CachedIcon from '@mui/icons-material/Cached';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CircularProgress from '@mui/material/CircularProgress';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import EastIcon from '@mui/icons-material/East';
import { visuallyHidden } from '@mui/utils';
import UpdateDialog from "./updateDialog";
import {useAsyncFetch} from "../../../system/useFetch";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {exportFile} from "../../../system/utils";

interface IWorkSheetSelector {
  names: string[];
  onSelected: (name: string | null) => void;
}

interface IProps {
  parentSize: {
    width: number;
    height: number;
  }
};

const getStatusIcon = (status: string): ReactElement => {
  switch (status) {
    case "ADD":
      return <AddCircleIcon color="success" />;
    case "UPDATE":
      return <ChangeCircleIcon color="primary" />;
    case "DELETE":
      return <RemoveCircleIcon color="error" />;
    default:
      return <CheckCircleIcon color="disabled" />;
  }
}

const headerStyle = {
  // backgroundColor: "#cfd8dc"
}

const GradesComponent = ({parentSize}: IProps): ReactElement => {
  const [loading, setLoading] = useState<boolean>(true);
  const [showAll, setShowAll] = useState<boolean>(true);
  const [filter, setFilter] = useState<string>("");
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof IMergeRecord>('grade');
  const [merged, setMerged] = useState<IMergeRecord[]>([]);
  const [getSpec] = useAsyncFetch<ISpecData>();
  const [toApply, setToApply] = useState<ISpecData[] | null>(null);
  const [dbGrades, setDbGrades] = useState<IGrades | null>(null);
  const [fileGrades, setFileGrades] = useState<IGrades | null>(null);
  const [workSheetSelector, setWorkSheetSelector] = useState<IWorkSheetSelector | null>(null);
  const maxHeight =parentSize.height;

  useEffect(() => {
    onReset();
  }, []);

  const onApply = () => {
    const categories: ISpecData[] = [];

    merged.forEach(record => {
      if (!record.checked) {

        return;
      }

      categories.push({
        categoryID: record.db ? record.db.specData?.categoryID || null : null,
        name: record.grade,
        content: {
          fields: [
            { name: "RECIPE", dataType: "TEXT", value: record.recipe, unit: "" },
            { name: "DOLO", dataType: "NUMBER", value: record.file?.elements.dolo || 0, unit: "lb" },
            { name: "HICAL", dataType: "NUMBER", value: record.file?.elements.lime || 0, unit: "lb" }
          ]
        }
      });
    });

    if(categories.length === 0) {

      return;
    }

    setToApply(categories);
  }

  const onReset = () => {
    setLoading(true);
    setFileGrades(null);
    setShowAll(true);

    getSpec({
      module: "alloys",
      query: "getSpecData",
      payload: {
        categoryName: "Lime By Grade"
      }
    }).then(response => {
      if (response && Array.isArray(response)) {
        const grades = convertToIGrades(response);
        setDbGrades(grades);
        setMerged(mergeLimeGrades(grades, null));
      }

      setLoading(false);
    });
  }

  const onClear = () => {
    setMerged(mergeLimeGrades(dbGrades || {}, fileGrades));
  }

  const onImportExcelFile = async(event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileData = await loadExcelFile(file, (names: string[]): Promise<string | null> => {

        return new Promise((resolve) => {
          setWorkSheetSelector({
            names,
            onSelected: (name: string | null) => {
              setWorkSheetSelector(null); // Close the selector after selection
              resolve(name);
            }
          });
        });
      });

      setFileGrades(fileData);
      setShowAll(false);
      setMerged(mergeLimeGrades(dbGrades || {}, fileData));
    }
  };

  const onExportExcelFile = async() => {
    const grades = merged.reduce((previousValue, currentValue) => {

      if (currentValue.db) {
        previousValue.push(currentValue.db);
      }

      return previousValue;
    }, [] as IGradeBase[]);

    if (grades.length === 0) {

      return;
    }

    const file = await exportGradesToExcel(grades.sort((a, b) => a.grade.localeCompare(b.grade)));
    const url = URL.createObjectURL(new Blob([file], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));

    exportFile(url, 'LimeByGrade.xlsx');
  }

  const sortHandler = (field: keyof IMergeRecord) => {
    if (orderBy === field) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrder('asc');
      setOrderBy(field);
    }
  }

  const onCopy = (record: IMergeRecord) => {
    const updatedRecord: IMergeRecord = {
      ...record,
      file: {
        ...record.db!,
      },
      status: "UPDATE",
      checked: true
    };

    setMerged(merged.map(v => v.grade === record.grade ? updatedRecord : v));
  };

  const onChecked = (record: IMergeRecord, checked: boolean) => {
    const updatedRecord: IMergeRecord = {
      ...record,
      checked,
      // status: checked ? "UPDATE" : "NONE"
    };
    
    if (updatedRecord.checked && !updatedRecord.file) {
      updatedRecord.file = {
        grade: record.grade,
        recipe: record.recipe,
        specData: null, // Config is not provided in the Excel file
        elements: {
          dolo: record.db?.elements.dolo || 0,
          lime: record.db?.elements.lime || 0
        }
      };
    }

    // evaluateStatus(updatedRecord);
    setMerged(merged.map(v => v.grade === record.grade ? updatedRecord : v));
  };

  const onValueChange = (record: IMergeRecord, field: "dolo" | "lime", value: string) => {
    const updatedRecord: IMergeRecord = {
      ...record,
      file: {
        grade: record.grade,
        recipe: record.recipe,
        specData: null, // Config is not provided in the Excel file
        elements: {
          ...record.file?.elements,
          [field]: parseFloat(value) || 0 // Ensure value is a number
        }
      },
      status: "UPDATE",
      checked: true
    };

    if (field === "dolo") {
      updatedRecord.file!.elements.lime = record.file?.elements.lime || 0; // Keep lime value unchanged
    } else {
      updatedRecord.file!.elements.dolo = record.file?.elements.dolo || 0; // Keep dolo value unchanged
    }

    updatedRecord.status = updatedRecord.db?.elements.dolo !== updatedRecord.file?.elements.dolo || updatedRecord.db?.elements.lime !== updatedRecord.file?.elements.lime ? "UPDATE" : "NONE";
    updatedRecord.checked = updatedRecord.status !== "NONE";

    setMerged(merged.map(v => v.grade === record.grade ? updatedRecord : v));
  }

  const onApplyUpdate = () => {
    getSpec({
      module: "alloys",
      query: "updateSpec",
      payload: {
        categoryName: "Lime By Grade",
        categories: toApply
      }
    }).then(response => {
      if (response && Array.isArray(response)) {
        const grades = convertToIGrades(response);
        setDbGrades(grades);
        setMerged(mergeLimeGrades(grades, fileGrades ?? {}));
      }

      setLoading(false);
    });

    setToApply(null);
  }

  const onCancelUpdate = () => {
    setToApply(null);
  }


  return (
    <div className="w-100 display-flex-column align-items-start gap-1" style={{ justifyContent: "stretch" }}>
      <div className="display-flex-row gap-1 align-items-center align-content-center" style={{ backgroundColor: "rgba(0, 0, 0, .1)", padding: "8px", width: "100%" }}>
        <Button startIcon={<CachedIcon />} size="small" color="secondary" onClick={onReset}>
          RELOAD
        </Button>

        <Button startIcon={<ClearAllIcon />} size="small" onClick={onClear}>
          CLEAR
        </Button>

        <Button startIcon={<FileUploadIcon />} component="label" size="small">
          Import
          <input type="file" accept=".xlsx, .xls" style={{ display: "none" }} onChange={onImportExcelFile}/>
        </Button>

        <Button startIcon={<FileDownloadIcon />}
                component="label" size="small"
                onClick={onExportExcelFile}
                disabled={!merged || merged.length === 0}>
          Export
        </Button>

        <Button startIcon={<SaveAltIcon />} size="small" color="success" onClick={onApply}>
          Apply
        </Button>

        <div className="flex-grow-1" />

        <FormControlLabel control={<Checkbox checked={showAll} onChange={e => setShowAll(e.target.checked)} />} label="Show All" />

        <TextField
          label="Filter"
          size="small"
          sx={{ m: 0, p: 0,}}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          // slotProps={{
          //   input: {
          //     endAdornment: <InputAdornment position="end">
          //       <IconButton></IconButton>
          //       <FilterAltIcon />
          //     </InputAdornment>,
          //   },
          // }}
          variant="standard"
        />

      </div>

      <div className="display-grid justify-content-center w-100 p-1">
        <TableContainer component={Paper} style={{ maxHeight }} >
          <Table  size="small" stickyHeader>
            <TableHead>
              <TableRow >
                <TableCell colSpan={4} style={headerStyle}/>
                <TableCell colSpan={2} align="center" style={headerStyle}>SYSTEM</TableCell>
                <TableCell style={headerStyle}/>
                <TableCell colSpan={2} align="center" style={headerStyle}>{fileGrades ? "EXCEL" : "MANUAL"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell style={{ ...headerStyle, top: 26 }}/>
                <TableCell style={{ ...headerStyle, top: 26 }}/>
                <TableCell
                  style={{ ...headerStyle, width: 200, top: 26 }}
                  sortDirection={orderBy === "grade" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "grade"}
                    direction={orderBy === "grade" ? order : 'asc'}
                    onClick={() => sortHandler("grade")}>
                  Grade
                    {orderBy === "grade" ? (
                      <Box component="span" style={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  style={{ ...headerStyle, width: 200, top: 26 }}
                  sortDirection={orderBy === "recipe" ? order : false}>
                  <TableSortLabel
                    active={orderBy === "recipe"}
                    direction={orderBy === "recipe" ? order : 'asc'}
                    onClick={() => sortHandler("recipe")}>
                    Recipe
                    {orderBy === "recipe" ? (
                      <Box component="span" style={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
                <TableCell style={{ ...headerStyle, width: 100, top: 26 }} align="right">Dolo</TableCell>
                <TableCell style={{ ...headerStyle, width: 100, top: 26 }} align="right">Lime</TableCell>
                <TableCell style={{ ...headerStyle, width: 40, top: 26 }} align="right"/>
                <TableCell style={{ ...headerStyle, width: 150, top: 26 }} align="right">Dolo</TableCell>
                <TableCell style={{ ...headerStyle, width: 150, top: 26 }} align="right">Lime</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {merged.filter(r => (showAll || r.status !== "NONE") && (filter === "" || r.grade.includes(filter) || r.recipe.includes(filter)))
                .sort(getComparator(order, orderBy))
                .map((v, index) => {
                const doloChanged = v.file ? v.file.elements.dolo !== v.db?.elements.dolo : false;
                const limeChanged = v.file ? v.file.elements.lime !== v.db?.elements.lime : false;
                const canCopy = v.db && (v.db.elements.dolo !== v.file?.elements.dolo || v.db.elements.lime !== v.file?.elements.lime) || false;
                const grade = v.grade;
                const hasSpecialChars = hasSpaceOrSpecialCharAtStart(grade) || hasSpaceOrSpecialCharAtEnd(grade);

                return(
                  <TableRow key={`row-${v.grade}-${index}`} sx={{
                    backgroundColor: index % 2 === 0 ? "rgba(0, 0, 0, 0.1)" : undefined, // Alternate row colors
                    '&:last-child td, &:last-child th': { border: 0 }
                  }}>
                    <TableCell component="th" scope="row">
                      <Checkbox checked={v.checked} onChange={e => onChecked(v, e.target.checked)}/>
                    </TableCell>
                    <TableCell>
                      {getStatusIcon(v.status)}
                    </TableCell>
                    <TableCell style={{ color: hasSpecialChars ? "red" : undefined }}>
                      {hasSpecialChars
                          ? <Tooltip title="Grade contains spaces at the start or end">
                              <p>{grade.replaceAll(" ", "·")}</p>
                          </Tooltip>
                          : grade}
                      {/*{grade}*/}
                    </TableCell>
                    <TableCell>
                      {v.recipe}
                    </TableCell>
                    <TableCell align="right">
                      {v.db ? v.db.elements.dolo : "---"}
                    </TableCell>
                    <TableCell align="right">
                      {v.db ? v.db.elements.lime : "---"}
                    </TableCell>

                    <TableCell align="center">
                      <IconButton size="small" onClick={() => onCopy(v)} disabled={!canCopy} color="info">
                        <EastIcon />
                      </IconButton>
                    </TableCell>

                    <TableCell align="right" >
                      <TextField
                        hiddenLabel
                        type="number"
                        value={v.file && v.file.elements.dolo ? v.file.elements.dolo : 0}
                        size="small"
                        onChange={e => onValueChange(v, "dolo", e.target.value)}
                        slotProps={{
                          input: {
                            sx: {
                              color: doloChanged ? "error.main" : "primary.main"
                            }}
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        hiddenLabel
                        type="number"
                        value={v.file && v.file.elements.lime ? v.file.elements.lime : 0}
                        size="small"
                        onChange={e => onValueChange(v, "lime", e.target.value)}
                        slotProps={{
                          input: {
                            sx: {
                            color: limeChanged ? "error.main" : "primary.main"
                          }}
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {workSheetSelector &&
        <WorkSheetSelector open={true}
                           workSheets={workSheetSelector.names}
                           onSelected={workSheetSelector.onSelected}/>
      }

      <Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={loading}>
        <CircularProgress color="secondary"/>
      </Backdrop>
      {toApply && <UpdateDialog categories={toApply} onApply={onApplyUpdate} onCancel={onCancelUpdate}/>}
    </div>
  );
}

export default GradesComponent;